import React, { useEffect, useState } from "react";
import { Form, Input, Select, DatePicker, Space } from 'antd';
import FormItem from "components/fields/FormItem";
import { connect, useSelector } from "react-redux";
import { 
	getOvertimeDetails, 
	doOvertimeCreate, 
	doOvertimeUpdate 
} from "../../../../redux/overtime/overtimeState";
import CrudButtons from "components/button/crudbuttons";
import dayjs from "dayjs";
import { 
	DATE_FORMAT_MDY, 
	TIME_FORMAT_24HM,
	DATE_FORMAT,
	TIME_FORMAT, 
	MODULE_OVERTIME,
} from "variables/constants";
import { requiredField } from "utils/crudSagaHelper";
import { getLabel } from "utils";

const OvertimeForm = ({
	getRecord, 
	doCreate, 
	doUpdate, 
	recordData, 
	requesting,
	success,
	id,
	onCancel,
	submitLabel,
	...props
}) => {
	
  	const { recordId, mode } = props;  	
  	const [form] = Form.useForm();
	const authUser = useSelector((state) => state.auth.user);
	const { RangePicker } = DatePicker;
	const [formData, setFormData] = useState({
        ots_user_id: null,
        ots_date_range: [],
        ots_approved_hours: null,
        ots_status_id: null,
    });

	const otsDateRange = Form.useWatch("ots_date_range", form);


  	useEffect(() => {
        getRecord(recordId);
	}, [getRecord, recordId]);
	
	useEffect(() => {
		if(recordData){
			form.resetFields();
			const { user:user, ...rest } = recordData;

			const initialValues = {
				...rest,
				...user?.userDetail,
				ots_user_id: recordData?.ots_user_id ?? null,
				ots_date_range: recordData?.ots_date_from && recordData?.ots_date_to
					? [dayjs(recordData.ots_date_from), dayjs(recordData.ots_date_to)]
					: [],
				ots_approved_hours: 
					recordData?.ots_approved_hours ?? recordData?.computed_hours ?? null,

				user_details: {
					ude_first_name: user?.user_detail?.ude_first_name ?? "",
					ude_last_name: user?.user_detail?.ude_last_name ?? "",
				},
			};	
	        form.setFieldsValue(initialValues);
		}
    }, [recordData, form]);

	useEffect(() => {
		if (otsDateRange?.length === 2) {
			const [from, to] = otsDateRange;
			const diffMinutes = to.diff(from, "minute");
			let hours = diffMinutes > 0 ? diffMinutes / 60 : 0;

			hours = Math.round(hours * 4) / 4;

			const current = form.getFieldValue("ots_approved_hours");
			if (!current || current === recordData?.computed_hours) {
			form.setFieldsValue({ ots_approved_hours: hours });
			}
		}
	}, [otsDateRange, form, recordData]);
		
  	const onSubmit = (formData) => {	
    	try {  
			if (formData.ots_date_range?.length === 2) {
				formData.ots_date_from = formData.ots_date_range[0].format(`${DATE_FORMAT} ${TIME_FORMAT}`);
      			formData.ots_date_to = formData.ots_date_range[1].format(`${DATE_FORMAT} ${TIME_FORMAT}`);
			}
			delete formData.ots_date_range;

			if (!formData.ots_user_id) {
				formData.ots_user_id = authUser?.id;
			}

			recordData.ots_id > 0 ? doUpdate(formData): doCreate(formData); 
    	} catch (error) {
      		console.error('Save failed:', error);
    	}
  	};
  
  	return (
		<Form form={form} 
			className={mode !== "view" ? "form" : ""}
			layout="vertical"
			onFinish={onSubmit}
		>   
			<FormItem
				name="ots_id"
				hidden
			>
				<Input type="hidden"/>
			</FormItem>

			<div className="grid grid-cols-3 gap-y-2 gap-x-4">
				{authUser?.user_level === 1 && mode !== "view" && (
					<FormItem
						className="font-medium mb-2"
						label="Employee"
						required
					>
						<FormItem
							name="ots_user_id"
							noStyle
							rules={[requiredField]}
							initialValue={formData?.ots_user_id || recordData?.ots_user_id || null}
						>
							<Select
								showSearch
								allowClear
								className="input-field"
								placeholder="Select Employee"
								onChange={(value) =>
									setFormData((prev) => ({ ...prev, ots_user_id: value }))
								}
								value={formData?.ots_user_id ?? recordData?.ots_user_id ?? undefined}
								options={recordData?.employeeList}
								optionFilterProp="label"
							/>
						</FormItem>
					</FormItem>
				)}

				<div className="col-span-2 mb-2">
					<div className="grid grid-cols-4 gap-2 w-full">
						<FormItem
							className="font-medium mb-2 col-span-3"
							name="ots_date_range" 
							label="Overtime Duration"
							rules={[requiredField]}
						>
							{mode !== "view" ? (
								<RangePicker
									className="input-field w-full"
									showTime={{ format: TIME_FORMAT_24HM }}
									format={`${DATE_FORMAT_MDY} ${TIME_FORMAT_24HM}`}
									valueFormat="YYYY-MM-DD HH:mm:ss" 
								/>
							) : (
								<label className="text-base label-value dark:text-white">
									{recordData?.ots_date_from && recordData?.ots_date_to
										? `${dayjs(recordData.ots_date_from).format(
											`${DATE_FORMAT_MDY} ${TIME_FORMAT_24HM}`
										)} - ${dayjs(recordData.ots_date_to).format(
											`${DATE_FORMAT_MDY} ${TIME_FORMAT_24HM}`
										)}`
									: ""}
								</label>
							)}
						</FormItem>

						<FormItem
							className="font-medium mb-2 col-span-1"
							name="ots_approved_hours"
							label="Hours"
							rules={[requiredField]}
						>
							{mode !== "view" ?
								<Input id="ots_approved_hours" 
									type="text"
									min={0}
									step={0.25}	
									className="input-field w-full" 
									 readOnly={true}
								/>
								:	
								<label className="text-base label-value dark:text-white">
									{recordData?.ots_approved_hours}
								</label>
							}
						</FormItem>
					</div>
				</div>

				<FormItem className="font-medium mb-2"
					name="ots_status_id" 
					label="Status"
					rules={[requiredField]}
				>
					{mode !== "view" ? 
						<Select id="ots_status_id"
							allowClear={true}
							showSearch
							className="input-field"
							options={recordData?.overtimeStatusList}
						/>
						: 
						<label className="text-base label-value dark:text-white">
							{getLabel(recordData?.overtimeStatusList, recordData?.ots_status_id)}
						</label>
					}
				</FormItem>
			</div>
			<div className="w-full flex justify-end">
				{(mode === "edit") && (
					<CrudButtons
						mode={mode}
						recordId={recordData?.ots_id ?? 0}
						isModal={true}
						modalId={recordData?.ots_id > 0 ? 'edit_overtime' : 'add_overtime'}
						cancelLabel="Cancel"
						doHideModal={onCancel}
						submitLabel={submitLabel}
						moduleId={MODULE_OVERTIME}
					/>
				)}
			</div>			 			
		</Form>	 	
  	);
};

const mapStateToProps = (state) => ({
  	recordData: state.overtime.overtimeDetails?.data,
  	requesting: state.overtime.overtimeDetails?.requesting,
  	success: state.overtime.overtimeDetails?.succcess,
});

const mapDispatchToProps = (dispatch) => ({
	getRecord: (id) => dispatch(getOvertimeDetails(id)),
  	doCreate: (data) => dispatch(doOvertimeCreate(data)),
  	doUpdate: (data) => dispatch(doOvertimeUpdate(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OvertimeForm);
